<script setup lang="ts">
import { ref, computed, watch, onBeforeUnmount } from 'vue'
import { useRoute, useRouter, RouterLink } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useToast } from 'primevue/usetoast'
import Popover from 'primevue/popover'
import BaseIcon from '@/components/base/BaseIcon.vue'
import BaseButton from '@/components/base/BaseButton.vue'
import ChangePasswordDialog from '@/components/auth/ChangePasswordDialog.vue'
import { ALERT_ICON, MODULE_ICONS } from '@/constants/icons'
import { hasFeatureAccess, isCialInternalUser } from '@/utils/auth'
import { userLabel } from '@/utils/users'
import { PLATFORM_COUNTRIES, isOpenToPlatformCountry, isPlatformCountry, platformCountryFlagClass, type PlatformCountry } from '@/platformCountry'
import type { RouteLocationRaw } from 'vue-router'

// --- Interfaces ---
interface NavItem {
  id: string
  to: string
  icon: string
  label: string
  visible?: boolean
}

interface NavSubItem {
  id: string
  to: string
  icon: string
  label: string
}

const authStore = useAuthStore()
const toast = useToast()

// --- Computed Properties ---
const user = computed(() => authStore.user)
const isSystemAdmin = computed(() => authStore.isSystemAdmin)
const isAccountAdmin = computed(() => authStore.isAccountAdmin)
const showSettings = computed(() => (isAccountAdmin.value || isSystemAdmin.value) && isOpenOnActivePlatform('/settings'))
const isCialUser = computed(() => isCialInternalUser(authStore.user?.email))
const hasFeatureAccessOrIsCialUser = computed(() => isCialUser.value || hasFeatureAccess(authStore.user, 'kyt'))
const hasPulseAccessOrIsCialUser = computed(() => isCialUser.value || hasFeatureAccess(authStore.user, 'pulse'))

// Menu links follow the same route meta as the platform guard, so a link is shown exactly when its
// page is open to the signed-in account's platform country (none while it isn't set).
function isOpenOnActivePlatform(to: RouteLocationRaw): boolean {
  return isOpenToPlatformCountry(router.resolve(to).meta.platformCountries, authStore.accountPlatformCountry)
}

const allNavItems = computed<NavItem[]>(() => [
  { id: 'company', to: '/company-lookup', icon: 'business', label: 'Empresa', visible: true },
  { id: 'person', to: '/person-lookup', icon: 'person', label: 'Pessoa', visible: true },
  { id: 'global-kyc', to: '/kyx/dashboard', icon: MODULE_ICONS.kyx, label: 'KYX', visible: true },
  { id: 'global-kyt', to: '/kyt', icon: MODULE_ICONS.kyt, label: 'KYT', visible: hasFeatureAccessOrIsCialUser.value },
  { id: 'global-entity-pulse', to: '/entity-pulse', icon: MODULE_ICONS.pulse, label: 'Pulse', visible: hasPulseAccessOrIsCialUser.value},
  { id: 'global-screening', to: '/global-screening', icon: 'travel_explore', label: 'Global Screening'},
  {
    id: 'global-alerts',
    to: '/alerts',
    icon: ALERT_ICON,
    label: 'Alertas',
    visible: true,
  },
  {
    id: 'global-investigations',
    to: '/investigations',
    icon: 'troubleshoot',
    label: 'Investigações',
    visible: true,
  },
  { id: 'global-sar', to: '/sars', icon: 'report_problem', label: 'SAR', visible: true },
  { id: 'ingestion', to: '/ingestion', icon: 'cloud_upload', label: 'Ingestão', visible: true },
])

const navItems = computed<NavItem[]>(() =>
  allNavItems.value.map((item) => ({ ...item, visible: item.visible !== false && isOpenOnActivePlatform(item.to) })),
)

const allKycSubItems: NavSubItem[] = [
  { id: 'global-kyc-dashboard', to: '/kyx/dashboard', icon: 'dashboard', label: 'Dashboard' },
  { id: 'global-kyc-operations', to: '/kyx/operations', icon: 'table_view', label: 'Operacoes' },
  { id: 'global-parecers', to: '/kyx/parecers', icon: 'gavel', label: 'Pareceres' },
  { id: 'global-kyc-batch-list', to: '/kyx/batch-upload', icon: 'upload_file', label: 'Arquivo em Lote' },
  { id: 'global-kyc-reports', to: '/kyx/reports', icon: 'summarize', label: 'Relatórios' },
]
const kycSubItems = computed(() => allKycSubItems.filter((subItem) => isOpenOnActivePlatform(subItem.to)))

const route = useRoute()
const router = useRouter()

const userMenuRef = ref<InstanceType<typeof Popover> | null>(null)
const isLoggingOut = ref(false)
const isSwitching = ref(false)

// Password Change Dialog
const isChangePasswordVisible = ref(false)

const openChangePassword = () => {
  userMenuRef.value?.hide()
  isChangePasswordVisible.value = true
}

const userInitials = computed(() => {
  if (!user.value) return 'U'
  const name = userLabel(user.value)
  return name
    .split(' ')
    .filter(Boolean)
    .map((word) => word.charAt(0))
    .join('')
    .toUpperCase()
    .slice(0, 2)
})

const myAccounts = computed(() => authStore.myAccounts)
const currentAccount = computed(() => authStore.account)
// Platform flags only help tell accounts apart when the user's accounts span more than one platform.
const showAccountPlatforms = computed(
  () => new Set(myAccounts.value.map((acc) => acc.platform_country_code).filter(isPlatformCountry)).size > 1,
)

// --- Methods ---
async function handleSwitchAccount(accountId: string) {
  if (isSwitching.value || accountId === currentAccount.value?.id) return

  isSwitching.value = true
  try {
    await authStore.switchAccount(accountId)
    toast.add({
      severity: 'success',
      summary: 'Troca de Conta',
      detail: 'Ambiente alterado com sucesso',
      life: 3000
    })
  } catch (error) {
    console.error('Switch account error:', error)
    toast.add({
      severity: 'error',
      summary: 'Erro',
      detail: 'Falha ao trocar de conta. Tente novamente.',
      life: 5000
    })
  } finally {
    isSwitching.value = false
  }
}
function isNavItemActive(itemId: string, defaultIsActive: boolean): boolean {
  if (itemId === 'company') {
    return defaultIsActive || route.path.startsWith('/company')
  }
  if (itemId === 'global-kyc') {
    return route.path.startsWith('/kyx')
  }
  if (itemId === 'global-kyt') {
    return route.path.startsWith('/kyt')
  }
  if (itemId === 'global-entity-pulse') {
    return route.path.startsWith('/entity-pulse')
  }
  if (itemId === 'global-screening') {
    return route.path.startsWith('/global-screening')
  }
  if (itemId === 'global-alerts') {
    return route.path.startsWith('/alerts')
  }
  if (itemId === 'global-investigations') {
    return route.path.startsWith('/investigations')
  }
  if (itemId === 'global-sar') {
    return route.path.startsWith('/sars')
  }
  if (itemId === 'settings') {
    return (
      route.path === '/settings' ||
      route.path.startsWith('/account-admin') ||
      route.path.startsWith('/system-admin')
    )
  }
  return defaultIsActive
}

const isKycSubItemActive = (itemTo: string): boolean => {
  return route.path === itemTo || route.path.startsWith(`${itemTo}/`)
}

// --- KYC Flyout (Teleport-based hover submenu) ---
const isKycPopoverVisible = ref(false)
const kycPopoverPosition = ref({ top: 0, left: 0 })
let kycHideTimeout: ReturnType<typeof setTimeout> | undefined

const clearKycHideTimeout = () => {
  if (kycHideTimeout !== undefined) {
    clearTimeout(kycHideTimeout)
    kycHideTimeout = undefined
  }
}

const handleKycNavEnter = (event: MouseEvent) => {
  clearKycHideTimeout()
  const el = event.currentTarget as HTMLElement
  if (el) {
    const rect = el.getBoundingClientRect()
    kycPopoverPosition.value = {
      top: rect.top + rect.height / 2,
      left: rect.right + 8,
    }
  }
  isKycPopoverVisible.value = true
}

const handleKycNavLeave = () => {
  kycHideTimeout = setTimeout(() => {
    isKycPopoverVisible.value = false
  }, 200)
}

const handleKycPopoverEnter = () => {
  clearKycHideTimeout()
}

const handleKycPopoverLeave = () => {
  kycHideTimeout = setTimeout(() => {
    isKycPopoverVisible.value = false
  }, 200)
}

const handleKycSubItemClick = (navigate: (e?: MouseEvent) => void, event: MouseEvent) => {
  isKycPopoverVisible.value = false
  navigate(event)
}

watch(() => route.path, () => {
  isKycPopoverVisible.value = false
})

onBeforeUnmount(() => {
  clearKycHideTimeout()
})

const toggleUserMenu = (event: Event) => {
  userMenuRef.value?.toggle(event)
}

const handleUserMenuNavigate = (navigate: (e?: MouseEvent) => void, event: MouseEvent) => {
  userMenuRef.value?.hide()
  navigate(event)
}

const handleLogout = async (): Promise<void> => {
  if (isLoggingOut.value) return
  isLoggingOut.value = true
  try {
    await authStore.logout()
    toast.add({
      severity: 'success',
      summary: 'Sucesso',
      detail: 'Sessão encerrada com sucesso',
      life: 3000,
    })
    router.push('/login')
  } catch (error) {
    console.error('Logout error:', error)
    toast.add({
      severity: 'error',
      summary: 'Erro',
      detail: 'Falha ao encerrar a sessão. Por favor, tente novamente.',
      life: 5000,
    })
  } finally {
    isLoggingOut.value = false
  }
}
</script>

<template>
  <aside
    class="flex flex-col h-screen w-22 fixed top-0 left-0 bg-surface-0 border-r border-surface-200 z-10 transition-all duration-300"
  >
    <div class="flex items-center justify-center p-2 border-b border-surface-200">
      <RouterLink to="/">
        <img
          src="/assets/int_one.png"
          alt="Logo"
          style="width: 82px; max-width: unset"
        />
      </RouterLink>
    </div>

    <nav class="flex flex-col flex-grow p-2 gap-1 overflow-y-auto custom-scrollbar">
      <template v-for="item in navItems" :key="item.to">
        <div
          v-if="item.visible !== false && item.id === 'global-kyc'"
          @mouseenter="handleKycNavEnter"
          @mouseleave="handleKycNavLeave"
        >
          <RouterLink :to="item.to" custom v-slot="{ href, navigate, isActive }">
            <a
              :href="href"
              @click="navigate"
              :aria-current="isNavItemActive(item.id, isActive) ? 'page' : undefined"
              class="group flex flex-col items-center justify-center py-2 rounded-md cursor-pointer transition-colors duration-200"
            >
              <BaseIcon
                class="text-2xl mb-1 p-2.5 rounded transition"
                :class="
                  isNavItemActive(item.id, isActive)
                    ? 'bg-primary-100 text-primary ring ring-primary-300'
                    : 'text-surface-700 group-hover:bg-primary-100'
                "
              >
                {{ item.icon }}
              </BaseIcon>
              <span
                class="text-[10px] leading-tight font-semibold text-center break-words w-full px-1"
                :class="isNavItemActive(item.id, isActive) ? 'text-primary' : 'text-surface-700'"
                >{{ item.label }}
              </span>
            </a>
          </RouterLink>
        </div>

        <RouterLink
          v-else-if="item.visible !== false"
          :to="item.to"
          custom
          v-slot="{ href, navigate, isActive }"
        >
          <a
            :href="href"
            @click="navigate"
            :aria-current="isNavItemActive(item.id, isActive) ? 'page' : undefined"
            class="group flex flex-col items-center justify-center py-2 rounded-md cursor-pointer transition-colors duration-200"
          >
            <BaseIcon
              class="text-2xl mb-1 p-2.5 rounded transition"
              :class="
                isNavItemActive(item.id, isActive)
                  ? 'bg-primary-100 text-primary ring ring-primary-300'
                  : 'text-surface-700 group-hover:bg-primary-100'
              "
            >
              {{ item.icon }}
            </BaseIcon>
            <span
              class="text-[10px] leading-tight font-semibold text-center break-words w-full px-1"
              :class="isNavItemActive(item.id, isActive) ? 'text-primary' : 'text-surface-700'"
              >{{ item.label }}
            </span>
          </a>
        </RouterLink>
      </template>
    </nav>

    <!-- Settings and User Avatar at Bottom -->
    <div class="p-2 border-t border-surface-200 space-y-2">
      <!-- Settings Icon (only visible to admins) -->
      <div v-if="showSettings" class="flex items-center justify-center">
        <RouterLink to="/settings" custom v-slot="{ href, navigate, isActive }">
          <a
            :href="href"
            @click="navigate"
            :aria-current="isNavItemActive('settings', isActive) ? 'page' : undefined"
            class="group flex flex-col items-center justify-center py-2 rounded-md cursor-pointer transition-colors duration-200"
            :class="
                isNavItemActive('settings', isActive)
                  ? 'bg-primary-100 text-primary ring ring-primary-300'
                  : 'text-surface-700 group-hover:bg-primary-100'
              "
          >
            <BaseIcon
              class="text-2xl mb-1 p-2.5 rounded transition text-surface-700 group-hover:bg-primary-100"
            >
              settings
            </BaseIcon>
            <span
              class="text-[10px] leading-tight font-semibold text-center break-words w-full px-1 text-surface-700"
            >
              Configuração
            </span>
          </a>
        </RouterLink>
      </div>

      <!-- User Avatar and Active Account Label -->
      <div class="flex flex-col items-center gap-1">
        <BaseButton
          text
          @click="toggleUserMenu($event)"
          aria-haspopup="menu"
          class="w-full flex items-center justify-center group hover:bg-none !p-0"
        >
          <div
            class="w-10 h-10 bg-primary-500 rounded-full flex items-center justify-center text-white font-medium select-none transition-colors shadow-sm hover:bg-primary-600 ring-1 ring-transparent group-hover:ring-primary-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-400"
          >
            {{ userInitials }}
          </div>
        </BaseButton>
        <span
          v-if="currentAccount"
          class="text-[9px] font-bold text-surface-500 uppercase tracking-tighter text-center px-1 truncate w-full"
          title="Conta Ativa"
        >
          {{ currentAccount.name }}
        </span>
      </div>

      <Popover ref="userMenuRef" placement="right-start" :pt="{ content: { class: 'p-0' } }">
        <div class="min-w-64 flex flex-col">
          <!-- User Profile Header -->
          <div class="p-4 border-b border-surface-100 bg-surface-50">
            <p class="font-bold text-surface-900 truncate">{{ user ? userLabel(user) : '' }}</p>
            <p class="text-xs text-surface-500 truncate">{{ user?.email }}</p>
          </div>

          <!-- Current Active Account Highlight -->
          <div v-if="currentAccount" class="p-3 bg-primary-50 border-b border-primary-100">
            <p class="text-[10px] font-bold text-primary-600 uppercase tracking-wider mb-1">Empresa Ativa</p>
            <div class="flex items-center gap-3">
              <BaseIcon class="text-primary text-xl">check_circle</BaseIcon>
              <div class="flex flex-col min-w-0">
                <span class="flex items-center gap-1.5 min-w-0">
                  <span class="text-sm font-bold text-surface-900 truncate">{{ currentAccount.name }}</span>
                  <span
                    v-if="showAccountPlatforms && isPlatformCountry(currentAccount.platform_country_code)"
                    v-tooltip.top="PLATFORM_COUNTRIES[currentAccount.platform_country_code].name"
                    :class="platformCountryFlagClass(currentAccount.platform_country_code)"
                    class="shrink-0 rounded-sm"
                  />
                </span>
                <span class="text-[10px] text-surface-600 truncate">CNPJ: {{ currentAccount.cnpj }}</span>
              </div>
            </div>
          </div>

          <!-- Other Accounts Switcher Section -->
          <div v-if="myAccounts.length > 1" class="p-2 max-h-64 overflow-y-auto">
            <p class="px-2 py-1 text-[10px] font-bold text-surface-400 uppercase tracking-wider">Alternar para</p>
            <template v-for="acc in myAccounts" :key="acc.id">
              <button
                v-if="acc.id !== currentAccount?.id"
                @click="handleSwitchAccount(acc.id)"
                class="w-full flex items-center gap-3 p-2 rounded-md hover:bg-surface-100 transition-colors group text-left"
                :disabled="isSwitching"
              >
                <BaseIcon class="text-xl text-surface-400 group-hover:text-primary transition-colors">business</BaseIcon>
                <div class="flex flex-col min-w-0">
                  <span class="flex items-center gap-1.5 min-w-0">
                    <span class="text-sm font-medium text-surface-900 truncate">{{ acc.name }}</span>
                    <span
                      v-if="showAccountPlatforms && isPlatformCountry(acc.platform_country_code)"
                      v-tooltip.top="PLATFORM_COUNTRIES[acc.platform_country_code as PlatformCountry].name"
                      :class="platformCountryFlagClass(acc.platform_country_code)"
                      class="shrink-0 rounded-sm"
                    />
                  </span>
                  <span class="text-[10px] text-surface-500 truncate">CNPJ: {{ acc.cnpj }}</span>
                </div>
              </button>
            </template>
          </div>

          <!-- Logout Action -->
          <div class="p-2 border-t border-surface-100 space-y-1">
            <RouterLink
              v-if="isOpenOnActivePlatform({ name: 'account-spending-summary' })"
              :to="{ name: 'account-spending-summary' }"
              custom
              v-slot="{ href, navigate }"
            >
              <a
                :href="href"
                class="flex w-full items-center rounded-md px-3 py-2 text-sm text-surface-700 transition-colors hover:bg-surface-100"
                @click="handleUserMenuNavigate(navigate, $event)"
              >
                <BaseIcon class="mr-2 text-lg">payments</BaseIcon>
                Gastos da conta
              </a>
            </RouterLink>

            <BaseButton
              v-if="user?.has_local_password"
              text
              @click="openChangePassword"
              class="w-full justify-start !px-3 !py-2 !text-surface-700 hover:!bg-surface-100"
            >
              <template #icon>
                <BaseIcon class="mr-2 text-lg">lock</BaseIcon>
              </template>
              Alterar Senha
            </BaseButton>

            <BaseButton
              text
              severity="danger"
              @click="handleLogout"
              class="w-full justify-start !px-3 !py-2"
              :loading="isLoggingOut"
            >
              <template #icon>
                <BaseIcon class="mr-2 text-lg">logout</BaseIcon>
              </template>
              Encerrar Sessão
            </BaseButton>
          </div>
        </div>
      </Popover>
    </div>

    <Teleport to="body">
      <!-- Change Password Dialog (Self Service) -->
      <ChangePasswordDialog v-model:visible="isChangePasswordVisible" />

      <Transition
        enter-active-class="transition-opacity duration-150 ease-out"
        leave-active-class="transition-opacity duration-100 ease-in"
        enter-from-class="opacity-0"
        leave-to-class="opacity-0"
      >
        <div
          v-if="isKycPopoverVisible"
          class="fixed min-w-52 rounded-xl border border-surface-200 bg-surface-0 shadow-xl p-2"
          :style="{
            top: kycPopoverPosition.top + 'px',
            left: kycPopoverPosition.left + 'px',
            transform: 'translateY(-50%)',
            zIndex: 1100,
          }"
          @mouseenter="handleKycPopoverEnter"
          @mouseleave="handleKycPopoverLeave"
        >
          <p class="px-2 py-1 text-[10px] font-bold uppercase tracking-widest text-surface-400">
            KYX
          </p>
          <div class="space-y-1">
            <RouterLink
              v-for="subItem in kycSubItems"
              :key="subItem.id"
              :to="subItem.to"
              custom
              v-slot="{ href, navigate }"
            >
              <a
                :href="href"
                @click="handleKycSubItemClick(navigate, $event)"
                class="group/item w-full flex items-center gap-2 rounded-lg px-3 py-2.5 text-sm transition-all duration-200"
                :class="
                  isKycSubItemActive(subItem.to)
                    ? 'bg-primary-50 text-primary-700 shadow-sm'
                    : 'text-surface-700 hover:bg-primary-50 hover:text-primary-600 hover:translate-x-0.5 hover:shadow-sm'
                "
              >
                <BaseIcon class="text-base transition-transform duration-200 group-hover/item:scale-110">{{ subItem.icon }}</BaseIcon>
                <span class="font-medium">{{ subItem.label }}</span>
              </a>
            </RouterLink>
          </div>
        </div>
      </Transition>
    </Teleport>
  </aside>
</template>

<style scoped>
a:hover {
  background-color: unset;
}
.custom-scrollbar::-webkit-scrollbar {
  width: 6px;
  height: 6px;
}
.custom-scrollbar::-webkit-scrollbar-track {
  background: transparent;
}
.custom-scrollbar::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 3px;
}
.custom-scrollbar::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}
</style>
